import React, { useState, useEffect, useRef } from "react";
import ApiManager from "../ApiManager/ApiManager";
import MapView from "./MapView/MapView";
import ManualAddressForm from "./ManualAddressForm/ManualAddressForm";
import { LoadingSpinner } from "./LoadingSpinner/LoadingSpinner";
import CorrelationIdManager from "../utils/CorrelationIdManager";
import "./MapInput.css";
import { TruckElectric } from "lucide-react";

const MapInput = ({
  label = "Address",
  onAddressSelect,
  initialValue,
  required = false,
  name = "address",
  validateValue = null,
  cityLocation = null,
  addressSuggestions = [],
  onSelectAddressSuggestion = null,
  usePublicApi = false
}) => {
  const [inputValue, setInputValue] = useState("");
  const [suggestions, setSuggestions] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [viewMap, setViewMap] = useState(true);
  const [shouldFetchSuggestions, setShouldFetchSuggestions] = useState(false);
  const [selectedLocation, setSelectedLocation] = useState({
    latitude: 0,
    longitude: 0,
    description: "",
  });
  const [error, setError] = useState("");
  const [correlationId, setCorrelationId] = useState(null);

  // Use ref to track if we've processed initialValue
  const initialProcessedRef = useRef(false);

  // Generate correlation ID when city location changes
  useEffect(() => {
    if (cityLocation) {
      setCorrelationId(CorrelationIdManager.generateTripCorrelationId());
    }
  }, [cityLocation]);

  const [manualAddress, setManualAddress] = useState({
    addressLine1: "",
    addressLine2: "",
    city: "",
    state: "",
    pincode: "",
  });

  const handleManualAddressChange = (name, value) => {
    const updatedAddress = {
      ...manualAddress,
      [name]: value,
    };
    setManualAddress(updatedAddress);
    setError("");

    if (onAddressSelect) {
      onAddressSelect(updatedAddress);
    }
  };

  const toggleView = () => {
    // Toggle between map and manual entry
    setViewMap(prev => !prev);
    console.log("Toggling view to:", !viewMap);
  };

  const [debouncedInput, setDebouncedInput] = useState("");

  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedInput(inputValue);
    }, 300);
    return () => clearTimeout(handler);
  }, [inputValue]);

  useEffect(() => {
    if (debouncedInput && shouldFetchSuggestions)
      fetchSuggestions(debouncedInput);
  }, [debouncedInput]);

  // Fix the infinite update loop in initialValue effect
  useEffect(() => {
    // Skip if no initialValue or if we've already processed it with the same values
    if (!initialValue) return;

    // Check if we're dealing with map coordinates or manual address
    const isMapCoordinates = initialValue.latitude !== undefined &&
                          initialValue.longitude !== undefined &&
                          initialValue.latitude !== null &&
                          initialValue.longitude !== null;

    if (isMapCoordinates) {
      setViewMap(true);
      setSelectedLocation({
        latitude: initialValue.latitude || 0,
        longitude: initialValue.longitude || 0,
        description: initialValue.description || "",
      });
      setInputValue(initialValue.description || "");
    } else {
      // For manual address data, keep map view as default but populate manual fields
      // This ensures we always start with map view for first-time address entry
      setManualAddress(initialValue);
      // Keep viewMap as true (default) - don't switch to manual view automatically
    }

    // Only call onAddressSelect during initial setup, not on every render
    if (!initialProcessedRef.current) {
      initialProcessedRef.current = true;
    }
  }, [initialValue?.latitude, initialValue?.longitude, initialValue?.description]);

  const fetchSuggestions = async (text) => {
    setIsLoading(true);
    try {
      // Use city location if available, otherwise use default
      const location = cityLocation
        ? `${cityLocation.lat},${cityLocation.lng}`
        : "28.5561,77.1";

      const params = new URLSearchParams({
        input: text,
        location: location,
        radius: 50000,
        stricbound: false,
      });

      // Use different API based on the usePublicApi prop
      const response = usePublicApi 
        ? await ApiManager.getAddressSuggestionsPublic(params, correlationId)
        : await ApiManager.getAddressSuggestions(params, correlationId);
      
      const data = await response.json();

      if (data.success && data.data && data.data.predictions) {
        setSuggestions(data.data.predictions);
      } else {
        setSuggestions([]);
      }
    } catch (err) {
      console.error("Error fetching suggestions:", err);
      setSuggestions([]);
    } finally {
      setIsLoading(false);
    }
  };

  const handleSelect = (suggestion) => {
    if (!suggestion || !suggestion.location) {
      console.error("Invalid suggestion format:", suggestion);
      return;
    }

    const location = {
      latitude: suggestion.location.lat || 0,
      longitude: suggestion.location.lng || 0,
      description: suggestion.description || "",
    };

    setSelectedLocation(location);
    setInputValue(suggestion.description || "");
    setSuggestions([]);
    setShouldFetchSuggestions(false);
    setError("");

    if (onAddressSelect) {
      onAddressSelect(location);
    }
  };

  const handleInputChange = (e) => {
    setShouldFetchSuggestions(true);
    setInputValue(e.target.value);

    if (e.target.value) {
      setError("");
    } else if (required) {
      setError(`${label} is required`);
    }
  };

  const validateInput = () => {
    if (required) {
      if (viewMap) {
        // In map view, validation happens on selectedLocation or inputValue
        if (!inputValue) {
          setError(`${label} is required`);
          return false;
        }
      } else {
        // In manual mode, validate the address fields
        if (!manualAddress.addressLine1 || !manualAddress.city || !manualAddress.state) {
          setError(`${label} fields are required`);
          return false;
        }
      }
    }

    // If there's a custom validation function, use it
    if (validateValue) {
      const customError = validateValue(viewMap ? selectedLocation : manualAddress);
      if (customError) {
        setError(customError);
        return false;
      }
    }

    return true;
  };

  // Add blur handler for validation
  const handleBlur = () => {
    validateInput();
  };

  return (
    <div className="mapInput">
      <div className="mapSection">
        <div className="mapHeader">
          <label className={required ? "required-label" : ""}>
            {label} {required && <span className="required-mark">*</span>}
          </label>
          <button
            type="button"
            className="custom-button"
            onClick={toggleView}
          >
            {viewMap ? "Switch to Manual Entry" : "Switch to Map Entry"}
          </button>
        </div>

        {viewMap ? (
          <div className="manualEntryForm">
            <div className="address-input-container">
              <input
                type="text"
                placeholder={`Enter ${label}${required ? ' *' : ''}`}
                value={inputValue}
                className={`input ${error ? 'input-error' : ''}`}
                onChange={handleInputChange}
                onBlur={handleBlur}
                required={required}
                name={name}
                data-testid={`${name}-input`}
              />
              {isLoading && (
                <div className="loading-indicator">
                  <LoadingSpinner
                    overlay={false}
                    size="small"
                    message="Loading suggestions..."
                    showProgress={false}
                  />
                </div>
              )}
              {/* Show address suggestions from reverse geocode if present */}
              {Array.isArray(addressSuggestions) && addressSuggestions.length > 0 && onSelectAddressSuggestion && (
                <ul className="suggestions-list">
                  {addressSuggestions.map((s, i) => (
                    <li
                      key={i}
                      className="suggestion-item"
                      onClick={() => onSelectAddressSuggestion(s)}
                    >
                      {s.formatted_address}
                    </li>
                  ))}
                </ul>
              )}
              {/* Show autocomplete suggestions as before */}
              {suggestions.length > 0 && (
                <ul className="suggestions-list">
                  {suggestions.map((s, i) => (
                    <li
                      key={i}
                      className="suggestion-item"
                      onClick={() => handleSelect(s)}
                    >
                      {s.description}
                    </li>
                  ))}
                </ul>
              )}
              {error && <div className="error-message">{error}</div>}
            </div>

            {/* Hidden input for form validation */}
            {required && (
              <input
                type="hidden"
                name={`${name}-validation`}
                required
                value={inputValue ? "valid" : ""}
              />
            )}
          </div>
        ) : (
          <ManualAddressForm
            formData={manualAddress}
            onChange={handleManualAddressChange}
            required={required}
            onBlur={handleBlur}
            error={error}
            name={name}
          />
        )}
        {error && <div className="error-message">{error}</div>}
      </div>
    </div>
  );
};

export default MapInput;
